import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  Dashboard,
  People,
  EventAvailable,
  Payments,
  Work,
  RocketLaunch,
  TrendingUp,
  School,
  Business,
  Settings,
  Chat as ChatIcon,
  Assignment,
  FolderSpecial,
  Article,
  Security,
  Domain,
  Badge,
  AccountTree,
  HowToReg,
  AccessTime,
  CalendarMonth,
  Handshake,
  Timeline,
  Receipt,
  Description,
  Apartment,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../store/slices/authSlice";
import {
  toggleSidebar,
  setSidebarOpen,
  showSnackbar,
} from "../../store/slices/uiSlice";
import {
  clearNotificationsAsync,
  fetchRolesAsync,
} from "../../store/slices/dataSlice";
import {
  receiveMessage,
  markMessagesReadAsync,
  fetchChatContacts,
  removeMessage,
} from "../../store/slices/chatSlice";
import { useSocket } from "../../context/SocketContext";
import { ROLES_CONFIG } from "../../types";
import { apiClient } from "../../api/apiClient";
import Sidebar from "../organisms/Sidebar";
import Topbar from "../organisms/Topbar";
import { checkPermission } from "../../utils/permissions";

const NAV_META: Record<string, { icon: React.ReactNode; label: string }> = {
  dashboard: { icon: <Dashboard />, label: "Dashboard" },
  tasks: { icon: <Assignment />, label: "Tasks" },
  projects: { icon: <FolderSpecial />, label: "Projects" },
  timesheets: { icon: <AccessTime />, label: "Timesheets" },
  chat: { icon: <ChatIcon />, label: "Chat" },
  employees: { icon: <People />, label: "Employees" },
  attendance: { icon: <HowToReg />, label: "Attendance" },
  leave: { icon: <EventAvailable />, label: "Leave" },
  payroll: { icon: <Payments />, label: "Payroll" },
  recruitment: { icon: <Work />, label: "Recruitment" },
  onboarding: { icon: <RocketLaunch />, label: "Onboarding" },
  performance: { icon: <TrendingUp />, label: "Performance" },
  training: { icon: <School />, label: "Training" },
  departments: { icon: <Domain />, label: "Departments" },
  designations: { icon: <Badge />, label: "Designations" },
  hierarchy: { icon: <AccountTree />, label: "Hierarchy" },
  reports: { icon: <Article />, label: "Reports" },
  settings: { icon: <Settings />, label: "Settings" },
  roles: { icon: <Security />, label: "Roles" },
  companies: { icon: <Business />, label: "All Companies" },
  mycompany: { icon: <Business />, label: "My Company" },
  clients: { icon: <Handshake />, label: "Clients" },
  invoices: { icon: <Receipt />, label: "Invoices" },
  tracking: { icon: <Timeline />, label: "Employee Tracking" },
  shifts: { icon: <CalendarMonth />, label: "Shift Management" },
  "company-documents": { icon: <Article />, label: "Company Documents" },
};

const GROUP_META: Record<string, string> = {
  dashboard: "Overview",
  reports: "Overview",
  companies: "Organization",
  mycompany: "Organization",
  departments: "Organization",
  designations: "Organization",
  hierarchy: "Organization",
  roles: "Organization",
  employees: "People",
  attendance: "People",
  tracking: "People",
  shifts: "People",
  leave: "People",
  timesheets: "People",
  recruitment: "Recruitment & Growth",
  onboarding: "Recruitment & Growth",
  performance: "Recruitment & Growth",
  training: "Recruitment & Growth",
  projects: "Work",
  tasks: "Work",
  clients: "Work",
  payroll: "Finance",
  invoices: "Finance",
  chat: "Communication & System",
  settings: "Communication & System",
  "company-documents": "Work",
};

const Shell: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const employees = useAppSelector((s) => s.data.employees);
  const sidebarOpen = useAppSelector((s) => s.ui.sidebarOpen);
  const notifications = useAppSelector((s) => s.data.notifications);
  const leaveRequests = useAppSelector((s) => s.data.leaveRequests) || [];

  const dbRoles = useAppSelector((s) => s.data.roles) || [];
  const activeContactId = useAppSelector((s) => s.chat?.activeContactId);
  const chatContacts = useAppSelector((s) => s.chat?.contacts || []);
  const totalUnreadChat = chatContacts.reduce(
    (acc: any, c: any) => acc + (c.unread || 0),
    0,
  );
  const { socket } = useSocket();

  const safeTrim = (s?: string) => (s || "").toLowerCase().trim();
  const safeLocal = (s?: string) =>
    safeTrim(s)
      .split("@")[0]
      .replace(/[^a-z0-9]/g, "");

  const matchEmployee = (e: any, u: any) => {
    if (!u || !e) return false;
    if (e.user === u._id || e.user?._id === u._id) return true;
    if (e.id === u.empId || e._id === u.empId) return true;

    const eEmail = safeTrim(e.email);
    const uEmail = safeTrim(u.email);
    if (eEmail && uEmail && eEmail === uEmail) return true;

    const eLocal = safeLocal(e.email);
    const uLocal = safeLocal(u.email);
    const uUser = safeLocal(u.username);
    const uName = safeLocal(u.name);
    const eName = safeLocal(e.name);

    if (eLocal && uLocal && eLocal === uLocal) return true;
    if (eLocal && uUser && eLocal === uUser) return true;
    if (uLocal && eName && uLocal === eName) return true;
    if (eName && uName && eName === uName) return true;
    if (eName && uUser && eName === uUser) return true;

    return false;
  };
  const empProfile = employees.find((e: any) => matchEmployee(e, user));

  // Safe role name from user
  const userRoleStr = user?.role ? user.role.trim().toLowerCase() : "";
  const sysRoleStr = empProfile?.sysRole
    ? empProfile.sysRole.trim().toLowerCase()
    : "";

  // Strictly check permissions from live Redux roles first
  const myRolePerm = dbRoles.find((r: any) => {
    if (!r.name) return false;
    const rName = r.name.trim().toLowerCase();
    const rLabel = r.label ? r.label.trim().toLowerCase() : rName;
    return (
      rName === userRoleStr ||
      rLabel === userRoleStr ||
      rName === sysRoleStr ||
      rLabel === sysRoleStr ||
      (user?.role && typeof user.role === 'string' && (r._id === user.role || r.id === user.role)) ||
      (user?.role && typeof user.role === 'object' && (r._id === (user.role as any)._id || r.id === (user.role as any).id || rName === ((user.role as any).name || "").trim().toLowerCase())) ||
      (empProfile?.sysRole && (r._id === empProfile.sysRole || r.id === empProfile.sysRole)) ||
      (empProfile?.role && (r._id === empProfile.role || r.id === empProfile.role))
    );
  });
  const isMasterAdminUser =
    user?.role?.toLowerCase() === "super admin" ||
    user?.role?.toLowerCase() === "master admin" ||
    empProfile?.role?.toLowerCase() === "super admin" ||
    empProfile?.role?.toLowerCase() === "master admin" ||
    empProfile?.sysRole?.toLowerCase() === "super admin" ||
    empProfile?.sysRole?.toLowerCase() === "master admin";
  const isAdmin = user?.role?.toLowerCase() === "admin" || isMasterAdminUser;
  const leavePerm = myRolePerm?.permissions?.find(
    (p: any) => p.module === "leave",
  );
  const canApproveLeave = isAdmin || (leavePerm?.canApprove ?? false);
  const dashboardPerm = myRolePerm?.permissions?.find(
    (p: any) => p.module === "dashboard",
  );
  const roleConfig = user
    ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG] || {
        label: isMasterAdminUser
          ? "Super Admin"
          : myRolePerm?.label || user.role,
        color: myRolePerm?.color || "blue",
      }
    : null;
  const pendingLeavesCount = leaveRequests.filter(
    (l: any) =>
      l.status === "Pending" && l.type !== "WFH" && l.leaveType !== "WFH",
  ).length;

  const [dynamicHistory, setDynamicHistory] = useState<any[]>(() => {
    try {
      const stored = localStorage.getItem(
        `hrms_dynamic_notifs_${user?._id || "default"}`,
      );
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    setDynamicHistory((prev) => {
      let changed = false;
      let newHistory = [...prev];

      if (pendingLeavesCount > 0) {
        const typeId = `pending_leaves_${pendingLeavesCount}`;
        if (!newHistory.find((n) => (n.typeId || n.id) === typeId)) {
          newHistory = newHistory.filter((n) => n.type !== "leave");
          newHistory.unshift({
            id: `${typeId}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            typeId,
            type: "leave",
            message: canApproveLeave
              ? `You have ${pendingLeavesCount} pending leave request(s) to review.`
              : `You have ${pendingLeavesCount} leave request(s) pending approval.`,
            time: "Just now",
            read: false,
          });
          changed = true;
        }
      } else {
        const beforeLen = newHistory.length;
        newHistory = newHistory.filter((n) => n.type !== "leave");
        if (newHistory.length !== beforeLen) {
          changed = true;
        }
      }

      chatContacts.forEach((c: any) => {
        if (c.unread > 0) {
          const typeId = `chat_unread_${c.id}_${c.unread}`;
          if (!newHistory.find((n) => (n.typeId || n.id) === typeId)) {
            newHistory = newHistory.filter(
              (n) =>
                !(
                  n.type === "chat" &&
                  (n.typeId || n.id).startsWith(`chat_unread_${c.id}_`)
                ),
            );
            newHistory.unshift({
              id: `${typeId}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
              typeId,
              type: "chat",
              message: `You have ${c.unread} unread message(s) from ${c.name}`,
              time: "Just now",
              read: false,
            });
            changed = true;
          }
        } else {
          const beforeLen = newHistory.length;
          newHistory = newHistory.filter(
            (n) =>
              !(
                n.type === "chat" &&
                (n.typeId || n.id).startsWith(`chat_unread_${c.id}_`)
              ),
          );
          if (newHistory.length !== beforeLen) {
            changed = true;
          }
        }
      });

      if (changed) {
        localStorage.setItem(
          `hrms_dynamic_notifs_${user?._id || "default"}`,
          JSON.stringify(newHistory),
        );
        return newHistory;
      }
      return prev;
    });
  }, [pendingLeavesCount, chatContacts, canApproveLeave]);

  const [seenIds, setSeenIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(
        `hrms_seen_notif_ids_${user?._id || "default"}`,
      );
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const allNotifications = [...dynamicHistory, ...notifications].map((n) => ({
    ...n,
    read: n.read || seenIds.has(n.id),
  }));

  const unseenCount = allNotifications.filter((n) => !n.read).length;

  const formatFallback = (name?: string) => {
    if (!name) return "User";
    let clean = name.includes("@") ? name.split("@")[0] : name;
    return clean
      .split(/[\.\-_]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };
  const displayName =
    empProfile?.name || formatFallback(user?.name || user?.username);
  const getInitials = (name: string) => {
    if (!name) return "U";
    const cleanName = name.split("@")[0];
    const parts = cleanName.split(/[\s.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return cleanName.substring(0, 2).toUpperCase();
  };
  const displayInitials = empProfile?.initials || getInitials(displayName);
  const displayColor = empProfile?.avColor || "blue";
  const displayDept = isMasterAdminUser
    ? "Super Admin"
    : empProfile?.role && empProfile.role !== "N/A" && empProfile.role !== ""
      ? empProfile.role
      : roleConfig?.label || user?.role || "Employee";

  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);

  const handleOpenNotifs = async (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(e.currentTarget);
  };

  const handleMarkAllRead = async () => {
    const allIds = allNotifications.map((n) => n.id);
    const newSeenIds = new Set([...seenIds, ...allIds]);
    setSeenIds(newSeenIds);
    try {
      localStorage.setItem(
        `hrms_seen_notif_ids_${user?._id || "default"}`,
        JSON.stringify([...newSeenIds]),
      );
      await apiClient.put("/notifications/read-all");
    } catch {}
  };

  const handleClearNotifs = async () => {
    try {
      setDynamicHistory([]);
      localStorage.removeItem(`hrms_dynamic_notifs_${user?._id || "default"}`);
      await dispatch(clearNotificationsAsync());
    } catch {}
  };

  useEffect(() => {
    if (isMobile) dispatch(setSidebarOpen(false));
    else dispatch(setSidebarOpen(true));
  }, [isMobile, dispatch]);

  // Global Chat Data Initialization
  useEffect(() => {
    dispatch(fetchChatContacts());
    dispatch(fetchRolesAsync());
  }, [dispatch]);

  // Global Chat Listener
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (m: any) => {
      const senderId = m.sender?._id || m.sender;
      const isMe = senderId === user?._id;
      const contactId = m.groupId ? m.groupId : senderId;

      dispatch(
        receiveMessage({
          contactId: contactId,
          text: m.text,
          time: new Date(m.createdAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          fileUrl: m.fileUrl,
          fileName: m.fileName,
          fileType: m.fileType,
          incrementUnread:
            !isMe &&
            (location.pathname !== "/app/chat" ||
              contactId !== activeContactId),
        }),
      );

      // Show notification if user is not on chat page OR if message is from a different contact, and NOT sent by self
      if (
        !isMe &&
        (location.pathname !== "/app/chat" || contactId !== activeContactId)
      ) {
        // Find sender name if possible
        const contact = chatContacts.find((c: any) => c.id === contactId);
        const senderName = contact ? contact.name : "Someone";
        const preview = m.text
          ? m.text.length > 30
            ? m.text.substring(0, 30) + "..."
            : m.text
          : m.fileName || "a file";

        dispatch(
          showSnackbar({
            message: m.groupId
              ? `New message in ${senderName}: ${preview}`
              : `New message from ${senderName}: ${preview}`,
            severity: "info",
          }),
        );
      } else if (
        !isMe &&
        location.pathname === "/app/chat" &&
        contactId === activeContactId
      ) {
        // If on chat page and it's the active contact, mark read immediately
        dispatch(markMessagesReadAsync(activeContactId));
      }
    };

    const handleMessageDeleted = (messageId: string) => {
      dispatch(removeMessage(messageId));
    };

    socket.on("new-message", handleNewMessage);
    socket.on("message-deleted", handleMessageDeleted);
    return () => {
      socket.off("new-message", handleNewMessage);
      socket.off("message-deleted", handleMessageDeleted);
    };
  }, [socket, dispatch, activeContactId, location.pathname, chatContacts]);

  useEffect(() => {
    if (socket && chatContacts && chatContacts.length > 0) {
      const groupIds = chatContacts
        .filter((c: any) => c.isGroup)
        .map((c: any) => c.id);
      if (groupIds.length > 0) {
        socket.emit("join-group-rooms", groupIds);
      }
    }
  }, [socket, chatContacts]);

  // Super Admin check
  const isMasterAdmin =
    empProfile?.role?.toLowerCase() === "super admin" ||
    empProfile?.role?.toLowerCase() === "master admin" ||
    isMasterAdminUser;

  let finalNavKeys = Object.keys(NAV_META).filter((k) => {
    // Role management is intentionally reserved for administrators. Do not
    // rely on a custom permission entry for this sensitive module.
    if (k === "roles" && !isAdmin) {
      return false;
    }
    if (k === "companies" && !isMasterAdmin) {
      return false;
    }
    if (k === "mycompany" && isMasterAdmin) {
      return false;
    }

    if (isMasterAdmin) return true;
    if (isAdmin) return true;

    // Use centralized checkPermission utility which handles ObjectIds, legacy roles, and employee overrides
    let hasAccess = checkPermission(user, dbRoles, k, 'canView', employees);

    // Legacy fallback for modules that haven't been added to the permissions matrix yet
    // but the user has access via their token's nav array
    if (!hasAccess) {
      if (!myRolePerm && user?.roleData?.nav?.includes(k)) {
        // Fallback to token only if DB roles haven't loaded yet (prevents UI flicker)
        hasAccess = true;
      } else if (myRolePerm?.nav?.includes(k)) {
        // Otherwise strictly rely on the live DB role's legacy nav array
        hasAccess = true;
      }
    }

    // Ultimate fallback for dashboard to ensure user isn't locked out of the app
    if (!hasAccess && k === "dashboard") {
      return true;
    }

    return hasAccess;
  });

  // DEBUG OUTPUT FOR ADMIN OR ANY USER TO SEE WHY
  console.log("DEBUG SHELL", {
    userRole: user?.role,
    myRolePermName: myRolePerm?.name,
    livePerms: myRolePerm?.permissions,
    finalNavKeys,
  });

  const GROUP_ORDER = [
    "Overview",
    "Organization",
    "People",
    "Recruitment & Growth",
    "Work",
    "Finance",
    "Communication & System",
    "Other",
  ];

  let flatNavItems = finalNavKeys.map((k: string) => ({
    key: k,
    ...NAV_META[k],
    group: GROUP_META[k] || "Other",
  }));

  // Group the items
  const groupedNavItemsObj = flatNavItems.reduce(
    (acc, item) => {
      if (!acc[item.group]) {
        acc[item.group] = [];
      }
      acc[item.group].push(item);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  // Sort groups and items within groups
  const groupedNavItems = Object.keys(groupedNavItemsObj)
    .sort((a, b) => {
      const aIdx = GROUP_ORDER.indexOf(a);
      const bIdx = GROUP_ORDER.indexOf(b);
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    })
    .map((groupName) => {
      const items = groupedNavItemsObj[groupName];
      items.sort((a, b) => {
        if (a.key === "dashboard") return -1;
        if (b.key === "dashboard") return 1;
        return a.label.localeCompare(b.label);
      });
      return { group: groupName, items };
    });

  const currentPage = location.pathname.split("/").pop() || "dashboard";
  const pageTitle = NAV_META[currentPage]?.label || "Dashboard";

  const handleNav = (key: string) => {
    navigate(`/app/${key}`);
    if (isMobile) dispatch(setSidebarOpen(false));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Sidebar
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={(val) => dispatch(setSidebarOpen(val))}
        roleConfig={roleConfig}
        navItems={groupedNavItems}
        currentPage={currentPage}
        handleNav={handleNav}
        handleLogout={handleLogout}
        user={user}
        displayName={displayName}
        displayDept={displayDept}
        displayInitials={displayInitials}
        displayColor={displayColor}
        pendingLeavesCount={pendingLeavesCount}
        totalUnreadChat={totalUnreadChat}
        profilePic={empProfile?.profilePic}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Topbar
          pageTitle={pageTitle}
          sidebarOpen={sidebarOpen}
          toggleSidebar={() => dispatch(toggleSidebar())}
          notifAnchor={notifAnchor}
          setNotifAnchor={setNotifAnchor}
          userAnchor={userAnchor}
          setUserAnchor={setUserAnchor}
          unseenCount={unseenCount}
          allNotifications={allNotifications}
          handleOpenNotifs={handleOpenNotifs}
          handleMarkAllRead={handleMarkAllRead}
          handleClearNotifs={handleClearNotifs}
          handleLogout={() => dispatch(logout())}
          handleNav={handleNav}
          user={user}
          displayName={displayName}
          displayInitials={displayInitials}
          displayColor={displayColor}
          roleConfig={roleConfig}
          profilePic={empProfile?.profilePic}
        />

        {/* Page content */}
        <Box
          sx={{
            flex: 1,
            background: "#f8fafc",
            p: { xs: 2, sm: 3 },
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Shell;
